import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Heart, Zap } from "lucide-react";
import DOMPurify from "dompurify";
import { useSEO } from "@/hooks/useSEO";

const VALUES = [
  { icon: ShieldCheck, title: "No Pressure, Ever",
    body: "You&rsquo;ll never hear a scripted urgency line from us. We win when you feel good about the sale. That&rsquo;s it." },
  { icon: Heart, title: "Local &amp; Accountable",
    body: "We live and work in Southwest Florida. Every house we buy is from a neighbor. That keeps us honest by default." },
  { icon: Zap, title: "Fast Because It Matters",
    body: "When you need out, you need out. We built our operation to get you a real number in hours, not weeks." },
];

export default function About() {
  useSEO({
    title: "About FlipPilot. Local Cash Home Buyers in Southwest Florida",
    description:
      "FlipPilot is a Southwest Florida cash home buyer. Learn about our mission, values, and how we approach every seller relationship with respect, transparency, and speed.",
    path: "/about",
    keywords:
      "about FlipPilot, Southwest Florida home buyer, we buy houses Cape Coral, local cash buyer SWFL",
  });

  return (
    <>
      <section className="pt-16 pb-12" data-testid="about-hero">
        <div className="mkt-container">
          <span className="mkt-chip">Our story</span>
          <h1 className="mkt-hero-title mt-6 max-w-3xl">
            Built by operators, <br />
            <span style={{ color: "var(--mkt-accent)" }}>not middlemen.</span>
          </h1>
          <p className="mt-6 text-lg text-[var(--mkt-text-muted)] max-w-2xl">
            FlipPilot is a Southwest Florida cash home-buying company. We buy
            houses directly from homeowners across Cape Coral, Fort Myers,
            Naples, and the surrounding areas. With no agents, no fees, and
            no drawn-out timelines.
          </p>
        </div>
      </section>

      <section className="py-16 bg-[var(--mkt-surface)]" data-testid="about-values">
        <div className="mkt-container">
          <span className="mkt-eyebrow">/ How we operate</span>
          <h2 className="mkt-section-title mt-3 max-w-2xl">Three commitments.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10">
            {VALUES.map(({ icon: Icon, title, body }, i) => (
              <div key={title} className="mkt-card p-6" data-testid={`value-${i}`}>
                <div
                  className="w-11 h-11 grid place-items-center rounded-full"
                  style={{ background: "var(--mkt-surface-2)", color: "var(--mkt-accent-dark)" }}
                >
                  <Icon size={18} strokeWidth={2.2} />
                </div>
                <h3 className="text-lg font-bold mt-4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(title) }} />
                <p
                  className="text-sm text-[var(--mkt-text-muted)] mt-2 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(body) }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20" data-testid="about-story">
        <div className="mkt-container max-w-3xl">
          <span className="mkt-eyebrow">/ Why this exists</span>
          <h2 className="mkt-section-title mt-3">A word from us.</h2>
          <div className="mt-6 space-y-4 text-[var(--mkt-text-muted)] leading-relaxed">
            <p>
              We started FlipPilot because every &ldquo;we buy houses&rdquo;
              experience we&rsquo;d watched friends and family go through felt
              the same: 1-800 numbers, algorithmic lowball offers, hidden fees
              stacked at closing, and re-negotiations after the inspection.
            </p>
            <p>
              We knew there was a better version. One where the buyer is
              local and accountable. Where the offer is backed by data you
              can actually read. Where the closing timeline works around
              you. Not the other way around.
            </p>
            <p>
              That&rsquo;s FlipPilot. Same team from your first call to the
              day funds hit your account. Same number on the paperwork as
              the one we quoted you up front. No surprises.
            </p>
            <p className="text-[var(--mkt-text)] font-medium">
              If you&rsquo;re thinking about selling, we&rsquo;d love to talk.
            </p>
          </div>
          <div className="mt-10">
            <Link to="/contact" className="mkt-btn mkt-btn-primary">
              Get Cash Offer
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
